"use client";

import { useRef } from "react";
import { motion, useScroll, type Variants } from "motion/react";
import { Container } from "@/components/ui/container";
import { Eyebrow } from "@/components/ui/eyebrow";
import { ScrollReanimateHeader } from "@/components/motion/scroll-reanimate-header";
import { TextRevealClip } from "@/components/motion/text-reveal-clip";
import { Parallax } from "@/components/motion/parallax";
import {
  KpiGaugePreview,
  FlowStageStripPreview,
  AgentRunPreview,
  ApprovalCardPreview,
} from "@/components/product-previews/workflow";
import { LIVE_WORKFLOW } from "@/lib/site";
import type { Dictionary } from "@/i18n/dictionaries";

// CrmErpVisual (step 02) is the only hand-built mini-UI we kept — its overdue-
// receivables table is already the right shape for ATTRIBUTE and porting an
// equivalent divetiqcl table would have meant duplicating divisor logic. The
// other 5 step visuals are real divetiqcl-ported components above.

// Cluster-level orchestrator. Children stagger in left→right after the
// outer card slide-in finishes (delayChildren ~ outer duration + a beat).
const cluster: Variants = {
  hidden: {},
  show: {
    transition: {
      delayChildren: 0.55,
      staggerChildren: 0.07,
    },
  },
};

const item: Variants = {
  hidden: { opacity: 0, x: -18 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const VIEWPORT = { once: true, margin: "-12% 0px" } as const;

// Per-step visual dispatch. Steps 01 + 06 reuse KpiGaugePreview (critical → ok)
// to visually "close the loop". Step 02 keeps the CrmErpVisual (overdue table).
// 03–05 use real divetiqcl-ported components: flow stage strip, agent run log,
// approval card.
const STEP_VISUALS: ReadonlyArray<() => React.ReactElement> = [
  () => (
    <KpiGaugePreview
      state="critical"
      title="Liquidity · 13-week forecast"
      numericValue={1.8}
      numericPrefix="−€"
      numericSuffix="M"
      numericDecimals={1}
      value="−€1.8M"
      trend="−12.4% vs target"
      metric="Week 6 deficit · policy floor breached"
    />
  ),
  () => <CrmErpVisual metric="€902K · 3 accounts · 28–42 days overdue" />,
  () => (
    <FlowStageStripPreview
      title="P2P · Working capital plan · 3 suppliers"
      stages={[
        { label: "PR", count: 12, state: "done" },
        { label: "RFQ", count: 9, state: "done" },
        { label: "PO", count: 7, state: "done" },
        { label: "GR", count: 5, state: "done" },
        { label: "INV", count: 4, state: "done" },
        { label: "HOLD", count: 3, state: "active" },
        { label: "PAY", count: 0, state: "upcoming" },
      ]}
      metric="10–15d deferral · no discount loss · €700K freed"
    />
  ),
  () => (
    <AgentRunPreview
      agent="Inventory Agent"
      lines={[
        { tool: "read_inventory", status: "ok", detail: "180d+ turn", ms: 142 },
        { tool: "fetch_supplier_terms", status: "ok", detail: "6 SKUs", ms: 88 },
        { tool: "score_scenarios", status: "ok", detail: "markdown vs return", ms: 312 },
        { tool: "rank_by_cash_return", status: "ok", child: true, ms: 47 },
        { tool: "draft_liquidation_plan", status: "running", detail: "writing…" },
        { tool: "queue_human_approval", status: "queued" },
      ]}
      counter={{ label: "Tokens used", value: 2140, decimals: 0 }}
    />
  ),
  () => (
    <ApprovalCardPreview
      title="CFO approval · combined plan"
      items={[
        { label: "Collect AR (3 accounts)", amount: "€1.8M" },
        { label: "Defer AP (3 suppliers)", amount: "€0.7M" },
        { label: "Liquidate stock (6 SKUs)", amount: "€2.1M" },
      ]}
      totalValue={4.6}
    />
  ),
  () => (
    <KpiGaugePreview
      state="ok"
      title="Liquidity · restored"
      numericValue={5.0}
      numericPrefix="€"
      numericSuffix="M"
      numericDecimals={1}
      value="€5.0M"
      trend="✓ +6.2% vs target"
      metric="t+11 days · audit complete"
    />
  ),
];

export function LiveWorkflow({ dict }: { dict: Dictionary }) {
  const olRef = useRef<HTMLOListElement>(null);
  // Drive the progress rail via scaleY (compositor-only) instead of height
  // (layout-triggering). Lenis already smooths scroll velocity; an extra
  // spring on top stacked easing curves and produced the visible stutter.
  const { scrollYProgress } = useScroll({
    target: olRef,
    offset: ["start 75%", "end 55%"],
  });

  return (
    <section id="workflow" className="relative py-20 sm:py-28">
      <Container>
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-end lg:gap-16">
          <ScrollReanimateHeader>
            <Eyebrow>{dict.liveWorkflow.eyebrow}</Eyebrow>
            <h2 className="mt-5 text-[34px] font-semibold leading-[1.06] tracking-[-0.04em] text-neutral-950 sm:text-[46px]">
              <TextRevealClip>{dict.liveWorkflow.heading}</TextRevealClip>
            </h2>
          </ScrollReanimateHeader>
          <div className="lg:max-w-md">
            <p className="text-base tracking-tight text-neutral-500 sm:text-lg">
              {dict.liveWorkflow.description}
            </p>
            <p className="mt-4 border-l-2 border-brand pl-4 text-sm italic text-neutral-700 sm:text-base">
              &ldquo;{dict.liveWorkflow.quote}&rdquo;
            </p>
          </div>
        </div>

        <ol ref={olRef} className="relative mt-16 space-y-10 sm:space-y-14">
          {/* Static rail */}
          <span
            aria-hidden
            className="pointer-events-none absolute left-[18px] top-2 hidden h-[calc(100%-32px)] w-px bg-neutral-200 sm:block"
          />
          {/* Scroll-driven progress line */}
          <motion.span
            aria-hidden
            style={{ scaleY: scrollYProgress, transformOrigin: "top" }}
            className="pointer-events-none absolute left-[18px] top-2 hidden h-[calc(100%-32px)] w-px bg-linear-to-b from-brand via-brand to-brand-tint sm:block"
          />
          {LIVE_WORKFLOW.steps.map((s, i) => {
            const t = dict.liveWorkflow.steps[i] ?? s;
            const renderVisual = STEP_VISUALS[i];
            return (
              <li
                key={s.index}
                className="relative grid gap-6 sm:grid-cols-[140px_1fr_minmax(0,360px)] sm:items-center sm:gap-10"
              >
                <motion.div
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <motion.span
                    className="grid size-9 shrink-0 place-items-center rounded-full border-2 border-brand bg-white text-[12px] font-bold text-brand-strong"
                    initial={{ boxShadow: "0 0 0 0 rgba(62,111,243,0.45)" }}
                    whileInView={{
                      boxShadow: [
                        "0 0 0 0 rgba(62,111,243,0.45)",
                        "0 0 0 10px rgba(62,111,243,0)",
                        "0 0 0 4px rgba(62,111,243,0.08)",
                      ],
                    }}
                    viewport={{ once: true, margin: "-10% 0px" }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  >
                    {s.index}
                  </motion.span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-strong">
                    {t.label}
                  </span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.55, delay: 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                    {t.agents}
                  </p>
                  <p className="mt-2 text-lg font-semibold leading-snug tracking-tight text-neutral-950 sm:text-xl">
                    {t.headline}
                  </p>
                  <p className="mt-2 text-sm tracking-tight text-neutral-500 sm:text-base">
                    {t.body}
                  </p>
                  {s.metric ? (
                    <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-semibold text-brand-strong">
                      {s.metric}
                    </p>
                  ) : null}
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 40, scale: 0.96 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.65, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Parallax offset={-15}>
                    {renderVisual ? renderVisual() : null}
                  </Parallax>
                </motion.div>
              </li>
            );
          })}
        </ol>
      </Container>
    </section>
  );
}

function VisualFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-[150px] w-full overflow-hidden rounded-2xl border border-neutral-200 bg-greyscale-25 p-4">
      {children}
    </div>
  );
}

function CrmErpVisual({ metric }: { metric?: string }) {
  const rows = [
    { name: "Acme Co", days: 42, amount: "€420K" },
    { name: "Lumen GmbH", days: 35, amount: "€288K" },
    { name: "Helia Group", days: 28, amount: "€194K" },
  ];
  return (
    <VisualFrame>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
      >
        <motion.p
          variants={item}
          className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500"
        >
          Top overdue · {metric}
        </motion.p>
        <ul className="mt-2 divide-y divide-neutral-100">
          {rows.map((r) => (
            <motion.li
              key={r.name}
              variants={item}
              className="flex items-center justify-between py-1.5 text-[11px]"
            >
              <span className="font-semibold text-neutral-950">{r.name}</span>
              <span className="text-neutral-500">{r.days}d</span>
              <span className="font-mono text-neutral-700">{r.amount}</span>
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </VisualFrame>
  );
}

